import React from "react";
import { Minus, Plus, ShoppingBag, RotateCcw } from "lucide-react";
import { TagPreview, STAKE_SIZES } from "../../lib/tagShapes";
import { TAG_ICONS } from "../../lib/tagIcons";

export const StepLabel = ({ n, children }) => (
  <h3 className="font-serif-display uppercase text-plum-deep text-xl md:text-2xl tracking-[0.08em] flex items-center gap-3">
    <span className="w-9 h-9 rounded-full bg-magenta text-cream flex items-center justify-center text-base shrink-0">{n}</span>
    {children}
  </h3>
);

const ShapeStep = ({ shapes, shapeId, shape, colour, stakeSize, basePrice, stakePrices, onSelectShape, onSelectSize }) => (
  <div data-testid="step-shape">
    <StepLabel n={1}>CHOOSE YOUR SHAPE</StepLabel>
    <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
      {shapes.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelectShape(s)}
          data-testid={`shape-option-${s.key}`}
          className={`rounded-2xl p-3 border-2 transition-all bg-warmwhite ${shapeId === s.id ? "border-magenta shadow-[0_10px_25px_-15px_rgba(236,38,117,0.7)]" : "border-plum-deep/10 hover:border-magenta/40"}`}
        >
          <TagPreview shapeKey={s.key} hex={shapeId === s.id ? (colour?.hex || "#EC2675") : "#E1CFF2"} className="w-full h-20" />
          <span className="block mt-2 font-serif-display uppercase text-[10px] tracking-[0.1em] text-plum-deep">{s.name}</span>
          <span className="block text-[10px] text-magenta font-semibold">
            {s.key === "etchery_plant_stake"
              ? `FROM $${Math.min(...Object.values(stakePrices).map(Number).filter(Boolean), Number(s.price || basePrice)).toFixed(2)}`
              : `$${Number(s.price || basePrice).toFixed(2)}`}
          </span>
          {Number(s.production_days) > 0 && (
            <span className="block text-[9px] text-plum-deep/50 uppercase tracking-[0.1em]">MADE IN ~{s.production_days} DAYS</span>
          )}
        </button>
      ))}
    </div>
    {shape?.key === "etchery_plant_stake" && (
      <div className="mt-5" data-testid="stake-size-picker">
        <p className="text-[11px] uppercase tracking-[0.2em] text-plum-deep/60 mb-2 font-semibold">STAKE SIZE</p>
        <div className="flex gap-2">
          {STAKE_SIZES.map((s) => (
            <button
              key={s}
              onClick={() => onSelectSize(s)}
              data-testid={`stake-size-${s}`}
              className={`px-5 py-2.5 rounded-full text-sm font-serif-display uppercase tracking-[0.1em] font-semibold transition-colors ${stakeSize === s ? "bg-magenta text-cream" : "bg-warmwhite border border-plum-deep/15 text-plum-deep hover:border-magenta"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ColourStep = ({ colours, colourId, colour, onSelectColour }) => (
  <div data-testid="step-colour">
    <StepLabel n={2}>CHOOSE YOUR COLOUR</StepLabel>
    <div className="mt-5 flex flex-wrap gap-3">
      {colours.map((c) => (
        <button
          key={c.id}
          onClick={() => onSelectColour(c.id)}
          data-testid={`colour-option-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          title={c.name}
          className={`w-12 h-12 rounded-full border-4 transition-transform hover:scale-110 ${colourId === c.id ? "border-plum-deep scale-110" : "border-plum-deep/15"}`}
          style={{ background: c.hex }}
        />
      ))}
    </div>
    {colour && <p className="mt-3 font-serif-display uppercase text-sm tracking-[0.15em] text-plum-deep/70">{colour.name}</p>}
  </div>
);

const TextStep = ({ line1, line2, maxLen, onLine1, onLine2 }) => (
  <div data-testid="step-text">
    <StepLabel n={3}>ADD YOUR TEXT</StepLabel>
    <div className="mt-5 space-y-4 max-w-md">
      <div>
        <input
          value={line1} maxLength={maxLen} onChange={(e) => onLine1(e.target.value)}
          placeholder="LINE 1 — e.g. CAFE AU LAIT" data-testid="tag-text-line1"
          className="w-full rounded-full px-6 py-3.5 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-magenta"
        />
        <p className="text-xs text-plum-deep/50 mt-1 pl-4">{line1.length}/{maxLen}</p>
      </div>
      <div>
        <input
          value={line2} maxLength={maxLen} onChange={(e) => onLine2(e.target.value)}
          placeholder="Line 2 (optional) — e.g. planted 2026" data-testid="tag-text-line2"
          className="w-full rounded-full px-6 py-3.5 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-magenta"
        />
        <p className="text-xs text-plum-deep/50 mt-1 pl-4">{line2.length}/{maxLen}</p>
      </div>
    </div>
  </div>
);

const IconStep = ({ icon, onPickIcon, onIconChange, onResetIcon }) => (
  <div data-testid="step-icon">
    <StepLabel n={4}>ADD AN ICON (OPTIONAL)</StepLabel>
    <div className="mt-5 flex flex-wrap gap-2">
      <button
        onClick={() => onPickIcon(null)}
        data-testid="icon-option-none"
        className={`px-4 h-12 rounded-2xl border-2 text-[10px] font-serif-display uppercase tracking-[0.1em] transition-colors ${!icon ? "border-magenta bg-warmwhite text-magenta" : "border-plum-deep/10 bg-warmwhite text-plum-deep/60 hover:border-magenta/40"}`}
      >
        NONE
      </button>
      {Object.entries(TAG_ICONS).map(([key, def]) => (
        <button
          key={key}
          onClick={() => onPickIcon(key)}
          data-testid={`icon-option-${key}`}
          title={def.label}
          className={`w-12 h-12 rounded-2xl border-2 flex items-center justify-center transition-colors bg-warmwhite ${icon?.key === key ? "border-magenta" : "border-plum-deep/10 hover:border-magenta/40"}`}
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6">
            <path d={def.d} fill={icon?.key === key ? "#EC2675" : "#4A1B4D"} fillOpacity={icon?.key === key ? 1 : 0.6} />
          </svg>
        </button>
      ))}
    </div>
    {icon && (
      <div className="mt-4 space-y-3 max-w-md bg-warmwhite border border-plum-deep/10 rounded-2xl p-4" data-testid="icon-controls">
        <p className="text-[11px] uppercase tracking-[0.18em] text-magenta font-semibold">DRAG THE ICON ON THE PREVIEW TO POSITION IT</p>
        <label className="flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-plum-deep/70 font-semibold">
          SIZE
          <input
            type="range" min="8" max="30" step="1" value={icon.size}
            onChange={(e) => onIconChange({ ...icon, size: Number(e.target.value) })}
            data-testid="icon-size-slider" className="flex-1 accent-magenta"
          />
        </label>
        <label className="flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-plum-deep/70 font-semibold">
          ROTATE
          <input
            type="range" min="-180" max="180" step="5" value={icon.rotation}
            onChange={(e) => onIconChange({ ...icon, rotation: Number(e.target.value) })}
            data-testid="icon-rotation-slider" className="flex-1 accent-magenta"
          />
          <button onClick={onResetIcon} data-testid="icon-reset-btn" className="text-plum-deep/40 hover:text-magenta" aria-label="Reset icon">
            <RotateCcw className="w-4 h-4" />
          </button>
        </label>
      </div>
    )}
  </div>
);

const QuantityStep = ({ qty, maxQty, unitPrice, onQty }) => (
  <div data-testid="step-quantity">
    <StepLabel n={5}>QUANTITY</StepLabel>
    <div className="mt-5 flex items-center gap-5">
      <div className="inline-flex items-center gap-4 bg-warmwhite border border-plum-deep/15 rounded-full px-3 py-2">
        <button onClick={() => onQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="w-9 h-9 rounded-full bg-blush text-plum-deep flex items-center justify-center hover:bg-magenta hover:text-cream transition-colors"><Minus className="w-4 h-4" /></button>
        <span className="font-serif-display text-2xl w-10 text-center" data-testid="qty-value">{qty}</span>
        <button onClick={() => onQty(Math.min(maxQty, qty + 1))} data-testid="qty-plus" className="w-9 h-9 rounded-full bg-blush text-plum-deep flex items-center justify-center hover:bg-magenta hover:text-cream transition-colors"><Plus className="w-4 h-4" /></button>
      </div>
      <span className="text-plum-deep/70">× ${unitPrice.toFixed(2)} = <span className="font-semibold text-plum-deep">${(unitPrice * qty).toFixed(2)} NZD</span></span>
    </div>
  </div>
);

export const BuilderSteps = (props) => (
  <>
    <ShapeStep {...props} />
    <ColourStep {...props} />
    <TextStep {...props} />
    <IconStep {...props} />
    <QuantityStep {...props} />
    <button
      onClick={props.onAdd}
      data-testid="add-to-order-btn"
      className="inline-flex items-center gap-3 rounded-full bg-magenta text-cream px-8 py-4 font-serif-display uppercase text-sm tracking-[0.18em] font-semibold hover:bg-plum transition-colors"
    >
      <ShoppingBag className="w-4 h-4" /> ADD TAG TO ORDER
    </button>
  </>
);
